import { Box } from '@mui/material';
import { useContext, useEffect, useMemo, useState } from 'react';

import { PartnerContact } from '@/components/common/contact_components/PartnerContact';
import { ConfigContext } from '@/Contexts/ConfigContext';
import { LocaleContext } from '@/Contexts/LocaleContext';
import { SelectedProgramContext } from '@/Contexts/SelectedProgramContext';
import { SnackbarContext } from '@/Contexts/SnackbarProvider';
import useContact from '@/hooks/useContact';
import usePartner from '@/hooks/usePartner';
import { ContactProps, ContactType, localizedTextEquals } from '@/models/Contact';
import { validateContact } from '@/utils/contactUtils';
import { SelectedPartnerContext } from '@/Contexts/SelectedPartnerContext';
import { TabSubPanelContent } from '@/components/myaccount/Applicant_MyAccount/TabSubPanelContent';
import { logError } from '@/utils/errorHandlerUtil';

interface PrimaryContactSectionProps {
  onUnsavedChanges?: () => void;
  onSave?: () => void;
}

export default function PrimaryContactSection({
  onUnsavedChanges,
  onSave,
}: PrimaryContactSectionProps) {
  const { saveContact, getContact } = useContact();
  const { patchPartnerRecord } = usePartner();
  const { setAlert } = useContext(SnackbarContext);
  const { selectedProgramId } = useContext(SelectedProgramContext);
  const { partnerInfo, updatePartnerInfo } = useContext(SelectedPartnerContext);
  const { contactTypes } = useContext(ConfigContext);
  const { translatedText } = useContext(LocaleContext);

  const [missingContactFields, setMissingContactFields] = useState<Array<keyof ContactProps>>([]);
  const [savingContact, setSavingContact] = useState<boolean>(false);
  const [currentContact, updateCurrentContact] = useState<ContactProps | null>(null);

  const partnerPrimaryContactType = useMemo(
    () =>
      contactTypes.find(
        (c: ContactType) =>
          localizedTextEquals(c.contactTypeName, 'PartnerPrimary') &&
          c.programId === selectedProgramId
      ),
    [contactTypes, selectedProgramId]
  );

  useEffect(() => {
    if (partnerInfo?.primaryContactId != null) {
      const fetchContact = async (contactId: number) => {
        const contact = await getContact(contactId);
        if (contact != null) updateCurrentContact(contact);
      };
      void fetchContact(partnerInfo?.primaryContactId);
    }
  }, [partnerInfo, partnerPrimaryContactType, getContact]);

  useEffect(() => {
    if (currentContact == null || partnerPrimaryContactType == null) return;
    const invalidFields = validateContact(currentContact, partnerPrimaryContactType);
    setMissingContactFields(invalidFields);
  }, [currentContact, partnerPrimaryContactType]);

  const handleContactChange = (contact: ContactProps) => {
    updateCurrentContact(contact);
    onUnsavedChanges?.();
  };

  const handleContactSave = async () => {
    if (currentContact == null) return;

    if (missingContactFields != null && missingContactFields.length > 0) {
      setAlert({
        message: translatedText.myaccount.errorHelperText,
        severity: 'error',
      });
      return;
    }

    setSavingContact(true);

    try {
      const contact = await saveContact(currentContact);

      if (contact != null && contact.id != null) {
        updateCurrentContact(contact);

        if (partnerInfo?.id != null && contact.id !== partnerInfo.primaryContactId) {
          const updatedPartner = await patchPartnerRecord(partnerInfo.id, {
            primaryContactId: contact.id,
          });
          updatePartnerInfo(updatedPartner);
        }

        setAlert({
          message: translatedText.myaccount.updatePrimaryContactSuccess,
          severity: 'success',
        });

        onSave?.();
      } else {
        setAlert({
          message: translatedText.myaccount.updatePrimaryContactError,
          severity: 'error',
        });
      }
    } catch (error) {
      logError(`Error saving contact for user.`, error);
      setAlert({
        message: translatedText.myaccount.updatePrimaryContactError,
        severity: 'error',
      });
    } finally {
      setSavingContact(false);
    }
  };

  return (
    <TabSubPanelContent
      headerText={translatedText.myaccount.primaryContactHeader}
      onSave={handleContactSave}
      saveDisabled={savingContact || currentContact == null}
      isPanelLoading={currentContact == null || partnerPrimaryContactType == null}
    >
      <Box display='flex' flexDirection='column' justifyContent='space-between' gap={2}>
        <PartnerContact
          id={'my-account-primary-contact'}
          contact={currentContact}
          contactType={partnerPrimaryContactType}
          onChange={handleContactChange}
        />
      </Box>
    </TabSubPanelContent>
  );
}
